import type { GarmentRateCard, ItemUnit } from '../api/masters';
import { orderBands, priceGarment, type UnitRateCard } from '../bookings/pricing';
import { formatAmount } from '../format/money';
import { encodeQr, fits, type QrSymbol } from '../format/qr';
import { withReference } from '../units/referenceNumber';

/**
 * A garment's hang tag (swing tag) — the card tied to the hanger, printed on the tag printer.
 *
 * <p>
 * <strong>The QR carries the serial and nothing else</strong>, because that is what the phone's
 * scanner and the website's both look up first: a scan opens this garment wherever it is read. The
 * serial is printed beside it, so a tag whose code will not scan can still be typed.
 * </p>
 *
 * <p>
 * <strong>A rate prints as its ladder, never as one figure.</strong> A rate here is bands of days,
 * and "₹8,000" cannot say for how long — the reason the stock list's single figure is not used.
 * Every amount is the shared pricer's answer for that many days (`priceGarment`, the counter's own
 * arithmetic, adjustment included), so the tag cannot quote a garment differently from the booking
 * screen. A printed rate does go stale when the shop changes its card; the QR beside it does not.
 * </p>
 *
 * <p>
 * Tags print as an image, so the rupee sign and any script print as they are. The layout here is
 * content, not columns — the tag is a small card drawn at its real size, not a roll of text.
 * </p>
 */

export type TagSize = '40x60' | '50x80';

/** The two common swing-tag stocks, portrait. The tag printer's media must match. */
export const tagSizes: Record<TagSize, { widthMm: number; heightMm: number; label: string }> = {
  '40x60': { widthMm: 40, heightMm: 60, label: '40 × 60 mm' },
  '50x80': { widthMm: 50, heightMm: 80, label: '50 × 80 mm' },
};

/**
 * How tall the garment code prints, in millimetres: as large as fits across the tag, up to 4.6 mm,
 * never below the 2.6 mm every tag used before. A code like BJB-36-01 is read off a rail from a
 * step away, so it earns the size; an older code like TUX_NAVY-40-NAVY-001 keeps the small size it
 * needs to fit. A bold monospace character is about 0.62 of its height wide, and the tag keeps 3 mm
 * of margin each side.
 */
export function serialMm(serial: string, widthMm: number): number {
  const fits = (widthMm - 6) / (0.62 * Math.max(1, serial.length));

  // Rounded down, so the size chosen never runs past the edge by a rounding hair.
  return Math.floor(Math.min(4.6, Math.max(2.6, fits)) * 100 + 1e-9) / 100;
}

/** One row of the rate ladder: what the customer keeps it for, and what that costs. */
export interface TagRateLine {
  label: string;
  value: string;
}

export type TagRate =
  /** Printed. */
  | { kind: 'lines'; lines: TagRateLine[]; more: boolean }
  /** The person printing may not see money — their tag carries no rate. */
  | { kind: 'hidden' }
  /** Nothing prices this garment — unfinished setup, so no rate rather than a zero. */
  | { kind: 'unpriced' };

export interface HangTag {
  serial: string;
  /**
   * What prints under the QR: the serial, with the shop's own number beside it in brackets when it
   * has one — "BLZ-38-03 (RN-7)" (core `units/referenceNumber.ts`). The QR stays the serial alone.
   */
  code: string;
  /** Null when the serial is too long for the symbol the tag has room for. */
  qr: QrSymbol | null;
  design: string;
  /** "Size 42 / Maroon" — the configuration, readable at a glance. */
  variant: string;
  rate: TagRate;
}

/** A tag has room for three bands and the open end; a longer card points to the counter. */
const maxBoundedBands = 3;

/**
 * The tag for one garment. `card` is null where the person printing may not see money
 * (`ItemUnit:ViewFinancials`), which prints a tag without one rather than refusing to print.
 */
export function hangTag(
  unit: Pick<ItemUnit, 'serialCode' | 'itemName' | 'variantDisplayName' | 'referenceNumber'>,
  card: GarmentRateCard | null,
): HangTag {
  return {
    serial: unit.serialCode,
    code: withReference(unit.serialCode, unit.referenceNumber),
    qr: fits(unit.serialCode) ? encodeQr(unit.serialCode) : null,
    design: unit.itemName,
    variant: unit.variantDisplayName,
    rate: card === null ? { kind: 'hidden' } : rateOf(card),
  };
}

/**
 * The ladder as a tag reads it, every figure the pricer's own for that many days. The design page
 * on screen reads it too, with room for every band (`maxBands`), so the two cannot word one card
 * two ways.
 */
export function rateOf(card: GarmentRateCard, maxBands: number = maxBoundedBands): TagRate {
  const pricing: UnitRateCard = { bands: card.bands, adjustmentPct: card.adjustmentPct };
  const bands = orderBands(card.bands);

  if (bands.length === 0) {
    return { kind: 'unpriced' };
  }

  const money = (amount: number) => `${card.currencySymbol}${formatAmount(amount)}`;
  const priceFor = (days: number) => priceGarment(pricing, days)?.amount ?? 0;
  const bounded = bands.filter((band) => band.upToDays !== null);
  const open = bands.find((band) => band.upToDays === null);
  const lines: TagRateLine[] = [];

  for (const band of bounded.slice(0, maxBands)) {
    const days = band.upToDays ?? 1;

    lines.push({
      label: days === 1 ? '1 day' : `Up to ${days} days`,
      value: money(priceFor(days)),
    });
  }

  const more = bounded.length > maxBands;

  if (open && !more) {
    const coveredTo = bounded.at(-1)?.upToDays ?? null;
    const perDay = open.perExtraDay ?? 0;

    if (coveredTo === null && perDay > 0 && perDay === open.amount) {
      // A per-day rate is a rate card with one open band — "₹1,200 a day", said as the shop says it.
      lines.push({ label: 'Per day', value: money(priceFor(1)) });
    } else if (perDay > 0) {
      if (coveredTo === null) {
        lines.push({ label: 'First day', value: money(priceFor(1)) });
      }

      const from = coveredTo ?? 1;

      lines.push({ label: 'Each extra day', value: money(priceFor(from + 1) - priceFor(from)) });
    } else {
      // A flat open band: one price however long, past the last bounded band.
      lines.push({
        label: coveredTo === null ? 'Any length' : `Over ${coveredTo} days`,
        value: money(priceFor((coveredTo ?? 0) + 1)),
      });
    }
  }

  return { kind: 'lines', lines, more };
}
